import { lazy } from 'react';
import { Badge } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import { str } from '../field-access';
import { NoteCountCell } from './note-count-cell';
import type { ResourceViewDefinition } from '../types';
import { statusTone } from './status-tone';
import { TranscriptionHeader } from './transcription-header';

/**
 * Transcription session view registration (GDN-2726) — replaces the standalone
 * transcript detail page and the bespoke transcriptions list.
 *
 * The list only became possible once `TranscriptionAdminService` gained a
 * canonical `list`; before that the type reported no list capability and the
 * generic list answered 400.
 *
 * Timestamps on this model are epoch floats (`start_timestamp`), not ISO
 * strings — `created_at` is excluded from the model and never persisted.
 */
export const transcriptionView: ResourceViewDefinition = {
  label: 'Transcription session',
  header: TranscriptionHeader,
  preview: lazy(() =>
    import('../../resource-renderers/transcription-resource-preview').then(
      m => ({ default: m.TranscriptionResourcePreview }),
    ),
  ),
  sections: lazy(() =>
    import('./transcription-sections').then(m => ({
      default: m.TranscriptionSections,
    })),
  ),
  enrich: lazy(() =>
    import('./transcription-enrichment').then(m => ({
      default: m.TranscriptionEnrichment,
    })),
  ),
  columns: [
    {
      key: 'start_timestamp',
      label: 'Started',
      render: value =>
        typeof value === 'number'
          ? formatDateTime(new Date(value * 1000).toISOString())
          : '—',
    },
    {
      key: 'job_status',
      label: 'Status',
      render: (value, row) => (
        <span className="flex items-center gap-1.5">
          <Badge color={statusTone(typeof value === 'string' ? value : null)}>
            {typeof value === 'string' ? value.replace('_', ' ') : '—'}
          </Badge>
          {str(row, 'deleted_at') && (
            <span className="text-xs font-medium text-red-600">deleted</span>
          )}
        </span>
      ),
    },
    {
      key: 'is_closed',
      label: 'Closed',
      render: value => (value === true ? 'Yes' : 'No'),
    },
    {
      key: 'chunks',
      label: 'Chunks',
      render: value => (Array.isArray(value) ? value.length : 0),
    },
    {
      // Derived, not on the record — supplied by the enrichment above.
      key: 'transcription_session_id',
      label: 'Notes',
      render: (_value, row) => <NoteCountCell row={row} />,
    },
    { key: 'recording_pipeline', label: 'Pipeline' },
    { key: 'user_id', label: 'User' },
  ],
  // scope="user" — the type has no patient dimension, and the endpoint honors
  // no other filter, so it offers none (see ResourceFilterKey).
  filters: [],
};
